/**
 * Matches the assets a case names to the entities the inventory actually holds,
 * and measures how far each one reaches.
 *
 * The match is a SEARCH, not a join. A case writes a display name — a hostname,
 * an email — while the Visualizer needs a canonical entity id, and there is no
 * shared key between them. So each name is looked up and the result is either a
 * confident match or an honest miss; a miss is shown, never hidden, because a
 * case about an asset the inventory does not track is itself a finding.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, of, catchError } from 'rxjs';
import { InventoryService } from '../../inventory/services/inventory.service';
import { PivotService } from '../../inventory/pivot/services/pivot.service';
import { EntityType } from '../../inventory/models/inventory.models';
import { PivotNodeKind, PivotRing, ScaleProfileKey } from '../../inventory/pivot/models/pivot.models';
import { CaseOrigin } from '../utils/case-origin';

/**
 * What the root actually touches, summarised by relation kind.
 *
 * Counts rather than members: a mini-map's job is to say "this account reaches
 * 14 endpoints and 3 applications" in one look. Drawing 14 named marks would
 * force an arbitrary choice of WHICH 14 and produce a picture nobody can read
 * at a glance — and the full canvas, one click away, already draws them
 * properly.
 */
export interface CaseReach {
  /** True degree of the root before any collapsing. */
  total: number;
  relations: { kind: PivotNodeKind; ring: PivotRing; total: number }[];
}

/** One case-named asset, after we have tried to find it in the inventory. */
export interface ResolvedOrigin {
  origin: CaseOrigin;
  /** Canonical entity id, or null when nothing matched. */
  entityId: number | null;
  /** The inventory's name for it — may differ in case or domain from the case's. */
  resolvedName: string | null;
  /** Total relationships, once known. Null while loading or unresolved. */
  degree: number | null;
}

/**
 * How many relation marks the reduced map will draw.
 *
 * Six is what fits on one circle at this size while staying readable; the
 * kinds are sorted by size first, so what is dropped is always the smallest.
 */
const MAX_RELATION_MARKS = 6;

@Injectable({ providedIn: 'root' })
export class CaseBlastRadiusService {
  private inventory = inject(InventoryService);
  private pivot = inject(PivotService);

  /**
   * Resolve every asset a case names, in one pass.
   *
   * `forkJoin` rather than sequential calls: these are independent lookups of
   * two or three names and the panel should not stagger in. Each inner call
   * swallows its own failure, so one unmatched name cannot blank the map.
   */
  public resolve(origins: CaseOrigin[]): Observable<ResolvedOrigin[]> {
    if (!origins.length) return of([]);
    return forkJoin(origins.map((o) => this.resolveOne(o)));
  }

  private resolveOne(origin: CaseOrigin): Observable<ResolvedOrigin> {
    const miss: ResolvedOrigin = { origin, entityId: null, resolvedName: null, degree: null };

    return this.inventory.listCanonicalEntitiesForPicker(origin.kind as EntityType, 5, origin.name).pipe(
      map((page) => {
        const match = this.bestMatch(page.value ?? [], origin);
        if (!match) return miss;
        return {
          origin,
          entityId: Number(match['id']),
          resolvedName: this.displayName(match, origin.kind),
          degree: null,
        };
      }),
      catchError(() => of(miss))
    );
  }

  /**
   * Pick the match, or decline to.
   *
   * `$search` is a SUBSTRING match, so "SRV-DB-02" also returns "SRV-DB-020".
   * Only an exact, case-insensitive hit is accepted: a blast radius drawn around
   * the wrong host is worse than one that admits it could not find the host.
   * A single fuzzy result is still declined — being alone does not make it
   * right.
   */
  private bestMatch(rows: Record<string, unknown>[], origin: CaseOrigin): Record<string, unknown> | null {
    const wanted = origin.name.trim().toLowerCase();
    for (const row of rows) {
      const candidates = [
        this.displayName(row, origin.kind),
        row['email'],
        row['principal_name'],
        row['endpoint_name'],
        row['application_name'],
      ];
      if (candidates.some((c) => typeof c === 'string' && c.trim().toLowerCase() === wanted)) {
        return row;
      }
    }
    return null;
  }

  private displayName(row: Record<string, unknown>, kind: CaseOrigin['kind']): string | null {
    const keys =
      kind === 'user'
        ? ['display_name', 'email', 'principal_name']
        : kind === 'endpoint'
          ? ['endpoint_name', 'display_name']
          : ['application_name', 'display_name'];
    for (const k of keys) {
      const v = row[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  }

  /**
   * What the root reaches, as relation kinds with counts.
   *
   * The neighbourhood rather than `/degree/`, because a lone node beside the
   * words "4 relationships" is not a blast radius — it names a size without
   * showing a shape, which is the one thing a map is for. `relations` is
   * already a per-kind index on this response, so the reduced map costs the
   * same single call the full canvas makes.
   */
  public reachOf(root: ResolvedOrigin, scale: ScaleProfileKey): Observable<CaseReach | null> {
    if (root.entityId === null) return of(null);

    return this.pivot
      .getNeighborhood(root.origin.kind, root.entityId, scale, root.resolvedName ?? root.origin.name)
      .pipe(
        map((n) => ({
          total: n.total_degree,
          // Strongest first, and only kinds actually present: a "0 Applications"
          // mark states an absence as though it were a finding, when it usually
          // means the estate has no application source feeding this entity.
          relations: n.relations
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, MAX_RELATION_MARKS)
            .map((r) => ({ kind: r.kind, ring: r.ring, total: r.total })),
        })),
        catchError(() => of(null))
      );
  }

  /**
   * How far each resolved entity reaches.
   *
   * Uses `/pivot/degree/`, which exists precisely to answer this without
   * building a neighbourhood — for the OTHER assets a case names we want the
   * SIZE of their blast radius, not its contents, and fetching the contents to
   * count them would cost a full traversal per node for a number.
   */
  public withDegrees(resolved: ResolvedOrigin[], scale: Parameters<PivotService['getDegree']>[2]) {
    const targets = resolved.filter((r) => r.entityId !== null);
    if (!targets.length) return of(resolved);

    return forkJoin(
      targets.map((r) =>
        this.pivot.getDegree(r.origin.kind, r.entityId as number, scale).pipe(
          map((d) => ({ id: r.entityId, total: d.total })),
          catchError(() => of({ id: r.entityId, total: null as number | null }))
        )
      )
    ).pipe(
      map((degrees) =>
        resolved.map((r) => {
          const hit = degrees.find((d) => d.id === r.entityId);
          return hit ? { ...r, degree: hit.total } : r;
        })
      )
    );
  }
}
